import React, { Component } from 'react'
import PropTypes from 'prop-types'
import BEM from '@iui-kit/utils/bem'
import styles from './Select.css'

const bem = new BEM(styles)

class Select extends Component {
  static propTypes = {
    className: PropTypes.string,
    valueClassName: PropTypes.string,
    dropdownClassName: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    ReplacementComponent: PropTypes.func,
    size: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
    custom: PropTypes.bool,
    disabled: PropTypes.bool,
    onOpen: PropTypes.func,
    onClose: PropTypes.func,
    children: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    placeholder: PropTypes.string,
    getHighlightedNode: PropTypes.func,
    isValid: PropTypes.bool
  }

  static defaultProps = {
    isValid: true
  }

  constructor(props) {
    super(props)

    this.state = {
      isOpen: false
    }
  }

  handleClickOutside = event => {
    if (this.wrapper && !this.wrapper.contains(event.target)) {
      this.handleOutside()
    }
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside)
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside)
  }

  closeDropdown = () => {
    const { onClose } = this.props

    this.setState(
      {
        isOpen: false
      },
      () => {
        onClose && onClose()
      }
    )
  }

  toggleDropdown = () => {
    const { onOpen, onClose } = this.props

    this.setState(
      {
        isOpen: !this.state.isOpen
      },
      () => {
        if (this.state.isOpen) {
          onOpen && onOpen()
        } else {
          onClose && onClose()
        }
      }
    )
  }

  handleOutside = () => {
    this.closeDropdown()
  }

  handleItemClick = child => event => {
    if (child.props.disabled) {
      return
    }

    this.closeDropdown()
    this.setState({
      selectedValue: this.getLabel(child.props.value)
    })
    this.props.onChange(child.props.value, event)
  }

  getLabel = value => {
    const { children, placeholder } = this.props
    let label = ''

    React.Children.toArray(children).forEach(child => {
      if (child.props.value === value) {
        label = child.props.label || child.props.children
      }
    })

    return label || placeholder
  }

  getItems = () => {
    return this.getAllChildren()
  }

  getAllChildren = () => {
    const { children, value } = this.props

    return React.Children.toArray(children).map(child => {
      if (child.props.custom) {
        return child
      } else {
        return React.cloneElement(child, {
          onClick: this.handleItemClick(child),
          selected: value === child.props.value
        })
      }
    })
  }

  render() {
    const { isOpen } = this.state
    const {
      className,
      valueClassName,
      dropdownClassName,
      size,
      value,
      placeholder,
      disabled,
      ReplacementComponent,
      isValid,
      ...otherProps
    } = this.props

    const label = this.getLabel(value)
    const items = this.getItems()

    return (
      <div
        ref={wrapper => {
          this.wrapper = wrapper
        }}
        className={bem
          .b('select')
          .m('isOpen', isOpen)
          .m('isDisabled', disabled === true)
          .m(size)
          .mix(className)
          .show()}
        {...otherProps}
      >
        <div
          style={{ outline: 'none' }}
          onClick={disabled ? null : this.toggleDropdown}
          role="listbox"
          tabIndex={0}
        >
          <div
            className={bem
              .b('select')
              .e('value-wrapper')
              .m('isOpen', isOpen)
              .m('error', !isValid)
              .m(size)
              .m('placeholder', label === placeholder)
              .mix(valueClassName)
              .show()}
            role="option"
          >
            <span
              className={bem
                .b('select')
                .e('value')
                .show()}
            >
              {ReplacementComponent ? <ReplacementComponent {...this.props} /> : label}
            </span>
          </div>
        </div>
        {!disabled && isOpen && (
          <div
            className={bem
              .b('select')
              .e('dropdown')
              .m('isOpen', isOpen)
              .m('error', !isValid)
              .mix(dropdownClassName)
              .show()}
          >
            {items}
          </div>
        )}
      </div>
    )
  }
}

const Option = ({
  children,
  disabled,
  selected,
  highlighted = false,
  getHighlightedNode,
  className,
  ...other
}) => {
  return (
    <div
      {...other}
      className={bem
        .b('select')
        .e('option')
        .m('isSelected', selected)
        .m('isHighlighted', highlighted)
        .m('isDisabled', disabled)
        .mix(className)
        .show()}
      ref={option => (highlighted ? getHighlightedNode(option) : '')}
    >
      {children}
    </div>
  )
}

Option.propTypes = {
  disabled: PropTypes.bool,
  selected: PropTypes.bool,
  highlighted: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.string,
  getHighlightedNode: PropTypes.func
}

Option.defaultProps = {
  disabled: false
}

export { Select, Option }
